import { createApi } from '@reduxjs/toolkit/query/react';

import type { Tenant } from '@features/tenants/api/tenants.types';
import { baseQuery } from '@shared/utils/base-query.utils';

import type { RefreshToken } from './authentication.types';

export interface LoginArgs {
  email: string;
  password: string;
  tenant?: string;
}
interface LoginResponse {
  accessToken: string;
  message: string;
  token: string;
  tenants: Tenant[];
}

interface LogoutResponse {
  message: string;
}
interface UserAgreementMutationBody {
  agreementId: number | null;
  email: string;
  tenantId: string;
}
export interface UserAgreementMutationArgs extends UserAgreementMutationBody {
  token: string;
}
interface UserAgreementResponse {
  id: number | null;
  policy: string;
  version: string;
  createdBy: string;
  creationDate: string;
  lastModifiedBy: string;
  lastModifiedDate: string;
}
interface UserAgreementQueryArgs {
  tenantId: string;
  token: string;
}
interface SavePasswordMutationArgs {
  email: string;
  token: string;
  newPassword: string;
}
export const authenticationApiSlice = createApi({
  reducerPath: 'authenticationApi',
  baseQuery,
  endpoints: (builder) => {
    return {
      getRefreshToken: builder.mutation<RefreshToken, void>({
        query: () => ({
          url: '/api/v1/auth/refresh',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }),
      }),
      login: builder.mutation<LoginResponse, LoginArgs>({
        query: ({ email, password, tenant }: LoginArgs) => ({
          url: '/api/v1/auth/login',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: { email, password, tenant },
        }),
      }),
      logout: builder.mutation<LogoutResponse, void>({
        query: () => ({
          url: '/api/v1/auth/logout',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }),
      }),
      getResetPasswordLink: builder.mutation({
        query: ({ email }: { email: string }) => ({
          url: '/api/v1/auth/resetPassword',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: { email },
          responseHandler: (response) => response.text(),
        }),
      }),
      savePassword: builder.mutation({
        query: ({ email, token, newPassword }: SavePasswordMutationArgs) => ({
          url: `/api/v1/auth/savePassword?token=${token}&email=${email}`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: { newPassword, token, email },
          responseHandler: (response) => response.text(),
        }),
      }),
      getUserAgreement: builder.query<UserAgreementResponse, UserAgreementQueryArgs>({
        query: ({ tenantId, token }) => ({
          url: `api/agreement/v1/current?tenant=${tenantId}`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }),
      }),
      acceptUserAgreement: builder.mutation<{ message: string }, UserAgreementMutationArgs>({
        query: ({ agreementId, email, tenantId, token }: UserAgreementMutationArgs) => ({
          url: 'api/agreement/v1/accept',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: { tenantId, agreementId, email },
        }),
      }),
    };
  },
});
export const {
  useLoginMutation,
  useGetRefreshTokenMutation,
  useGetResetPasswordLinkMutation,
  useSavePasswordMutation,
  useGetUserAgreementQuery,
  useAcceptUserAgreementMutation,
} = authenticationApiSlice;
